import { apiClient } from './apiClient';

// API 응답에서 받는 실제 데이터 형식 (목록용)
interface AdminPostApiResponse {
  post_id: number;
  title: string;
  category_name: string;
  writer_name: string;
  created_at: string;
  visible: boolean;
}

// 게시글 상세 조회 API 응답 형식
interface AdminPostDetailResponse {
  post_category: string;
  post_title: string;
  post_content: string;
  writer_name: string;
  writer_profile_image_url: string;
  created_at: string;
  updated: boolean;
  can_update: boolean;
  attachment_urls: string[];
  reply_cnt: number;
  view_cnt: number;
  lecture_id?: number;
  lecture_title?: string;
  lecture_chapter?: string;
  lecturer_id?: number;
  lecturer_name?: string;
  study_category_id?: number;
  study_category_name?: string;
  replies: any[];
}

// 관리자 게시글 인터페이스
export interface AdminPost {
  id: number;
  title: string;
  content: string;
  category: string;
  createdAt: string;
  updatedAt?: string;
  status: 'ACTIVE' | 'HIDDEN' | 'DELETED';
  views: number;
  commentsCount: number;
  authorId: number;
  authorName: string;
}

// API 요청 파라미터
export interface AdminPostListParams {
  page?: number;
  size?: number;
  postCategoryId?: number | null; // null 값도 허용
  from?: string; // yyyy-mm-dd 형식
  to?: string; // yyyy-mm-dd 형식
  status?: string; // 'ACTIVE' | 'HIDDEN' | 'DELETED'
  search?: string;
}

// API 응답 인터페이스
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

// 페이지네이션 응답 인터페이스 (실제 API 응답 구조)
interface PaginatedApiResponse {
  content: AdminPostApiResponse[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

// API 응답을 AdminPost 인터페이스로 변환하는 함수
const convertToAdminPost = (apiPost: AdminPostApiResponse): AdminPost => ({
  id: apiPost.post_id,
  title: apiPost.title,
  content: '', // API에서 제공하지 않음
  category: apiPost.category_name,
  createdAt: apiPost.created_at,
  status: apiPost.visible ? 'ACTIVE' : 'HIDDEN',
  views: 0, // API에서 제공하지 않음
  commentsCount: 0, // API에서 제공하지 않음
  authorId: 0, // API에서 제공하지 않음
  authorName: apiPost.writer_name
});

// 관리자 게시글 API 서비스
export const adminPostApi = {
  // 관리자 게시글 생성 - POST /api/admin/posts
  createAdminPost: async (postData: {
    postCategoryId: number;
    title: string;
    content: string;
    pinned: boolean;
    exposed: boolean;
  }): Promise<ApiResponse<{
    post_id: number;
    title: string;
    category_name: string;
    writer_name: string;
    created_at: string;
    visible: boolean;
  }>> => {
    try {
      /// console.log('관리자 게시글 생성 API 호출', postData);
      
      const { data: result } = await apiClient.post<ApiResponse<{
        post_id: number;
        title: string;
        category_name: string;
        writer_name: string;
        created_at: string;
        visible: boolean;
      }>>('/api/admin/posts', postData, {
        withCredentials: true
      });
      
      /// console.log('관리자 게시글 생성 API 응답:', {result});
      
      return result;
    } catch (error) {
      /// console.error('관리자 게시글 생성 실패:', error);
      throw error;
    }
  },

  // 관리자 게시글 목록 조회 - GET /api/admin/posts/list/admin
  getAdminPosts: async (params: AdminPostListParams = {}): Promise<{
    content: AdminPost[];
    totalPages: number;
    totalElements: number;
    currentPage: number;
    size: number;
  }> => {
    try {     
      /// console.log('관리자 게시글 목록 조회 API 호출', params);
      
      // 쿼리 파라미터 구성
      const queryParams = new URLSearchParams();
      
      if (params.page !== undefined) queryParams.append('page', params.page.toString());
      if (params.size !== undefined) queryParams.append('size', params.size.toString());
      if (params.postCategoryId) queryParams.append('postCategoryId', params.postCategoryId.toString());
      if (params.from) queryParams.append('from', params.from);
      if (params.to) queryParams.append('to', params.to);
      if (params.status) queryParams.append('visible', params.status === 'ACTIVE' ? 'true' : 'false');
      if (params.search) queryParams.append('search', params.search);

      const url = `/api/admin/posts/list/admin${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const { data: result } = await apiClient.get<ApiResponse<PaginatedApiResponse>>(url, {
        withCredentials: true
      });
      
      /// console.log('관리자 게시글 목록 API 응답:', {result});
      
      if (result.code !== 200) {
        throw new Error(`API error: ${result.message}`);
      }

      // API 응답 데이터를 AdminPost 인터페이스로 변환
      const convertedPosts = result.data.content.map(convertToAdminPost);
      
      return {
        content: convertedPosts,
        totalPages: result.data.totalPages,
        totalElements: result.data.totalElements,
        currentPage: result.data.number,
        size: result.data.size
      };
    } catch (error) {
      /// console.error('관리자 게시글 목록 조회 실패:', error);
      throw error;
    }
  },

  // 관리자 게시글 상세 조회 - GET /api/posts/{postId}
  getAdminPost: async (postId: number): Promise<{
    id: number;
    title: string;
    content: string;
    category: string;
    authorName: string;
    createdAt: string;
    lectureId?: number;
    lectureTitle?: string;
    lecturerId?: number;
    lecturerName?: string;
    studyCategoryId?: number;
    studyCategoryName?: string;
  }> => {
    try {
      /// console.log('관리자 게시글 상세 조회 API 호출', postId);
      
      const { data: result } = await apiClient.get<ApiResponse<AdminPostDetailResponse>>(`/api/posts/${postId}`, {
        withCredentials: true
      });
      
      /// console.log('관리자 게시글 상세 API 응답:', {result});
      
      if (result.code !== 200) {
        throw new Error(`API error: ${result.message}`);
      }

      // API 응답 데이터를 필요한 형태로 변환
      return {
        id: postId,
        title: result.data.post_title,
        content: result.data.post_content,
        category: result.data.post_category,
        authorName: result.data.writer_name,
        createdAt: result.data.created_at,
        lectureId: result.data.lecture_id,
        lectureTitle: result.data.lecture_title,
        lecturerId: result.data.lecturer_id,
        lecturerName: result.data.lecturer_name,
        studyCategoryId: result.data.study_category_id,
        studyCategoryName: result.data.study_category_name
      };
    } catch (error) {
      /// console.error('관리자 게시글 상세 조회 실패:', error);
      throw error;
    }
  },

  // 관리자 게시글 수정 - PUT /api/admin/posts/{postId}
  updateAdminPost: async (postId: number, postData: {
    postCategoryId: number;
    title: string;
    content: string;
    pinned: boolean;
    exposed: boolean;
  }): Promise<void> => {
    try {
      /// console.log('관리자 게시글 수정 API 호출', {postId, postData});
      
      await apiClient.put(`/api/admin/posts/${postId}`, postData, {
        withCredentials: true
      });
    } catch (error) {
      /// console.error('관리자 게시글 수정 실패:', error);
      throw error;
    }
  },

  // 게시글 상태 변경 - PATCH /api/admin/posts/{postId}/visible
  togglePostVisibility: async (postId: number, visible: boolean): Promise<void> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('visible', visible.toString());
      
      const url = `/api/admin/posts/${postId}/visible?${queryParams.toString()}`;
      
      await apiClient.patch(url, {}, {
        withCredentials: true
      });
    } catch (error: any) {
      /// console.error('게시글 상태 변경 실패:', error);
      throw new Error(error.message || '게시글 상태 변경 중 오류가 발생했습니다.');
    }
  },

  // 게시글 삭제 - DELETE /api/admin/posts/{postId}
  deletePost: async (postId: number, reason?: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/admin/posts/${postId}`, {
        data: reason ? { reason } : undefined,
        withCredentials: true
      });
    } catch (error) {
      /// console.error('게시글 삭제 실패:', error);
      throw error;
    }
  }
};
